import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orgAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Building2, Mail, Phone, Lock, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import PasswordInput from '@/components/shared/PasswordInput';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const setUserAndToken = useAuthStore((s) => s.setSessionFromToken || s.loadUser);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ password: '', phone: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    orgAPI.previewInvite(token)
      .then(setPreview)
      .catch(e => setError(e.response?.data?.detail || 'Invitation not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await orgAPI.acceptInvite(token, form);
      localStorage.setItem('auth_token', r.access_token);
      // Reload user state into store, then route to recruiter dashboard
      if (useAuthStore.getState().loadUser) {
        await useAuthStore.getState().loadUser();
      }
      navigate('/recruiter');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center" data-testid="accept-invite-loading">
      <Loader2 className="w-6 h-6 text-slate-500 dark:text-zinc-500 animate-spin" />
    </div>
  );

  if (error && !preview) return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-[#111114] border border-red-500/30 rounded-xl p-6" data-testid="accept-invite-error">
        <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 mb-3" />
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Invitation problem</h2>
        <p className="text-slate-500 dark:text-zinc-400 text-sm">{error}</p>
        <button onClick={() => navigate('/login')} className="mt-4 text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white">Back to login →</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center p-4" data-testid="accept-invite-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="max-w-md w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Join {preview?.organization_name}</h1>
            <p className="text-slate-500 dark:text-zinc-500 text-xs">You&apos;ve been invited as a recruiter</p>
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3 mb-5 flex items-center gap-2 text-sm">
          <Mail className="w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <span className="text-slate-600 dark:text-zinc-300">{preview?.email}</span>
          <span className="text-slate-500 dark:text-zinc-600 text-xs ml-auto">@{preview?.organization_domain}</span>
        </div>

        {error && <div className="bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg mb-3 text-sm" data-testid="accept-form-error">{error}</div>}

        <form onSubmit={submit} className="space-y-3" data-testid="accept-invite-form">
          <div>
            <label className="text-xs text-slate-500 dark:text-zinc-500 mb-1 block">Full name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="accept-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-zinc-500 mb-1 block flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone (international)</label>
            <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+15551234567" required className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="accept-phone-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-zinc-500 mb-1 block flex items-center gap-1.5"><Lock className="w-3 h-3" /> Set a password</label>
            <PasswordInput
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              testId="accept-password-input"
              placeholder="At least 6 characters"
              required
              minLength={6}
              autoComplete="new-password"
              icon={null}
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg pl-3 pr-11 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
            />
          </div>
          <button type="submit" disabled={submitting} className="w-full mt-2 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-zinc-100 transition disabled:opacity-50 flex items-center justify-center gap-2" data-testid="accept-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Accept invite <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
        <p className="text-slate-500 dark:text-zinc-600 text-[11px] mt-4 text-center">By accepting you agree to Numuv&apos;s Terms. Your access is granted by the recruiter head — no additional admin approval needed.</p>
      </div>
    </div>
  );
}
